import { BoldIcon } from "@images/richText/boldIcon"
import { BulletListIcon } from "@images/richText/bulletListIcon"
import { ItalicIcon } from "@images/richText/italicIcon"
import { LinkIcon } from "@images/richText/linkIcon"
import { OrderedListIcon } from "@images/richText/orderedListIcon"
import { UnderlineIcon } from "@images/richText/underlineIcon"
import { word } from "@modules/core/utils/i18n"
import { HUButton } from "@modules/ui/components/huButton"
import { HUInput } from "@modules/ui/components/huInput"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { ThemeMode } from "@modules/ui/styles/theme"
import { Editor } from "@tiptap/react"
import { useState } from "react"
import { useTheme } from "styled-components"

export const RichTextMenuBar = ({ editor }: { editor: Editor | null }) => {
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")

  const toast = useToast()
  const theme = useTheme()

  if (!editor) {
    return null
  }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const setLink = () => {
    // empty
    if (linkUrl === "" || linkUrl === undefined) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      setShowLinkModal(false)
      setLinkUrl("")

      return
    }
    if (!isValidUrl(linkUrl)) {
      toast.show({ severity: "error", detail: word("global.richTextForm.linkError") })
      return
    }

    // update link
    try {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run()
      setShowLinkModal(false)
      setLinkUrl("")
    } catch (e: any) {
      toast.show({ detail: e.message })
    }
  }

  const buttonClassName = (isActive: boolean) =>
    `cursor-pointer ${isActive ? "bg-primary-500" : (theme.mode === ThemeMode.ATHLETE ? "bg-neutralColor-200" : "bg-neutralColor-100") + " hover:surface-300"}`

  return (
    <div className="flex flex-row gap-3 align-items-center mb-2 mt-1">
      <div className="flex flex-row align-items-center">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 border-round-left-sm ${buttonClassName(editor.isActive("heading", { level: 1 }))}`}
          style={{ height: 32 }}
        >
          <HUText fontStyle="TL">{word("global.richTextForm.blockTypes.title")}</HUText>
        </button>

        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 border-round-right-sm ${buttonClassName(editor.isActive("heading", { level: 2 }))}`}
          style={{ height: 32 }}
        >
          <HUText fontStyle="TS">{word("global.richTextForm.blockTypes.subtitle")}</HUText>
        </button>
      </div>

      <div className="flex flex-row align-items-center">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`px-1 border-round-left-sm ${buttonClassName(editor.isActive("bold"))}`}
          style={{ height: 32 }}
        >
          <BoldIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`px-1 ${buttonClassName(editor.isActive("italic"))}`}
          style={{ height: 32 }}
        >
          <ItalicIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={`px-1 border-round-right-sm ${buttonClassName(editor.isActive("underline"))}`}
          style={{ height: 32 }}
        >
          <UnderlineIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
      </div>

      <div className="flex flex-row align-items-center">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`px-1 border-round-left-sm ${buttonClassName(editor.isActive("bulletList"))}`}
          style={{ height: 32 }}
        >
          <BulletListIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`px-1 border-round-right-sm ${buttonClassName(editor.isActive("orderedList"))}`}
          style={{ height: 32 }}
        >
          <OrderedListIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
      </div>

      <div className="flex flex-row align-items-center">
        <button
          onClick={() => {
            setLinkUrl(editor.getAttributes("link").href ?? "")
            setShowLinkModal(true)
          }}
          className={`px-1 border-round-sm ${buttonClassName(editor.isActive("link"))}`}
          style={{ height: 32 }}
        >
          <LinkIcon color={theme.textPrimary} className="vertical-align-middle" />
        </button>
      </div>

      <HUDialog
        visible={showLinkModal}
        header={word("global.richTextForm.blockTypes.link")}
        onHide={() => setShowLinkModal(false)}
        style={{ width: 650, height: 230 }}
      >
        <HUInput
          className="w-full"
          onChange={(e) => {
            setLinkUrl(e.target.value)
          }}
          placeholder={word("global.form.linkInputPlaceholder")}
          value={linkUrl}
        />

        <div className="flex flex-row justify-content-end gap-2 mt-4">
          <HUButton
            onClick={() => {
              setShowLinkModal(false)
              setLinkUrl("")
            }}
            type="Default"
            colorType="Quaternary"
            size="M"
            text={word("global.cancel")}
          />

          <HUButton
            type="Default"
            colorType="Primary"
            size="M"
            text={word("global.check")}
            onClick={() => {
              setLink()
            }}
          />
        </div>
      </HUDialog>
    </div>
  )
}
